import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { addGlass, DEFAULT_GLASSES_TARGET, GLASS_ML, mlToOz, removeLastGlass, type WaterLog } from "../lib/water";

interface Props {
  todayLogs: WaterLog[];
  onChange: () => void;
}

export default function WaterTracker({ todayLogs, onChange }: Props) {
  const [busy, setBusy] = useState(false);
  const count = todayLogs.length;
  const target = DEFAULT_GLASSES_TARGET;
  const ml = count * GLASS_ML;
  const oz = mlToOz(ml);

  async function add() {
    setBusy(true);
    try {
      await addGlass();
      onChange();
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (todayLogs.length === 0) return;
    setBusy(true);
    try {
      const last = todayLogs[todayLogs.length - 1];
      await removeLastGlass(last.id);
      onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-5 pt-4 border-t border-slate-800">
      <div className="flex items-center justify-between mb-2 text-xs">
        <span className="flex items-center gap-1.5 text-slate-300 font-medium">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-400" />
          Water
        </span>
        <span className="tabular-nums">
          <span className="text-slate-100">{count}</span>
          <span className="text-slate-500"> / {target} · {Math.round(oz)} oz</span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 grid grid-cols-8 gap-1">
          {Array.from({ length: Math.max(target, count) }).map((_, i) => (
            <GlassIcon key={i} filled={i < count} />
          ))}
        </div>
        <button
          onClick={remove}
          disabled={busy || count === 0}
          className="w-8 h-8 rounded-full bg-slate-800 text-slate-300 flex items-center justify-center disabled:opacity-30 active:bg-slate-700"
          aria-label="Remove last glass"
        >
          <Minus className="w-4 h-4" strokeWidth={2.5} />
        </button>
        <button
          onClick={add}
          disabled={busy}
          className="w-8 h-8 rounded-full bg-sky-500 text-slate-950 flex items-center justify-center disabled:opacity-50 active:bg-sky-600"
          aria-label="Add a glass"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

function GlassIcon({ filled }: { filled: boolean }) {
  // Simple glass shape: trapezoidal cup. Filled = sky-400, empty = outlined slate-700.
  return (
    <svg viewBox="0 0 20 24" className="w-full h-9" preserveAspectRatio="xMidYMid meet">
      <path
        d="M3 3 L17 3 L15.5 21 Q15.4 22 14.3 22 L5.7 22 Q4.6 22 4.5 21 Z"
        fill={filled ? "#38bdf8" : "transparent"}
        stroke={filled ? "#38bdf8" : "#334155"}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {filled && (
        // subtle highlight to convey water sheen
        <rect x="5.5" y="5" width="2" height="11" rx="1" fill="rgba(255,255,255,0.25)" />
      )}
    </svg>
  );
}
